import React, { useState } from "react";
import { Ruler, X, Check, AlertTriangle, Wand2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { patchUpload, autoDetectUploadScale } from "../lib/api";

export const IMPERIAL_SCALES = [
    '3/32" = 1\'',
    '1/8" = 1\'',
    '3/16" = 1\'',
    '1/4" = 1\'',
    '3/8" = 1\'',
    '1/2" = 1\'',
    '3/4" = 1\'',
    '1" = 1\'',
    '1 1/2" = 1\'',
    '3" = 1\'',
    '1" = 10\'',
    '1" = 20\'',
    '1" = 30\'',
    '1" = 40\'',
    '1" = 50\'',
    '1" = 60\'',
    '1" = 100\'',
];

export const METRIC_SCALES = [
    "1:5",
    "1:10",
    "1:20",
    "1:25",
    "1:50",
    "1:75",
    "1:100",
    "1:125",
    "1:200",
    "1:250",
    "1:500",
    "1:1000",
    "1:2000",
    "1:2500",
    "1:5000",
];

/**
 * Floating indicator in the top-right of the PDF viewport:
 *  - red "Set scale" chip when the file has no scale
 *  - blue chip with the current scale when one is set
 * Clicking opens a popover to pick from common scales for the project's
 * unit system, or type a custom one. The choice is PATCH'd onto the upload.
 */
export default function ScaleIndicator({ file, unitSystem = "imperial", onChange }) {
    const [open, setOpen] = useState(false);
    const [custom, setCustom] = useState("");
    const [saving, setSaving] = useState(false);
    const [detecting, setDetecting] = useState(false);

    if (!file) return null;

    const autoDetect = async () => {
        setDetecting(true);
        try {
            const res = await autoDetectUploadScale(file.id);
            if (res?.detected && res?.scale) {
                onChange && onChange(res.upload);
                toast.success(
                    `Detected ${res.scale} from ${res.summary?.picks?.length || 0} dimensions`,
                );
                setOpen(false);
            } else {
                toast.error(
                    res?.reason ||
                        "Couldn't auto-detect a scale. Pick one manually.",
                );
            }
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Auto-detect failed. Try picking a scale manually.",
            );
        } finally {
            setDetecting(false);
        }
    };

    const hasScale = !!file.scale;
    const scales = unitSystem === "metric" ? METRIC_SCALES : IMPERIAL_SCALES;

    const save = async (value) => {
        const trimmed = (value || "").trim();
        if (!trimmed) return;
        setSaving(true);
        try {
            const updated = await patchUpload(file.id, {
                scale: trimmed,
                scale_source: "manual",
            });
            onChange && onChange(updated);
            toast.success(`Scale saved: ${trimmed}`);
            setOpen(false);
            setCustom("");
        } catch {
            toast.error("Couldn't save scale");
        } finally {
            setSaving(false);
        }
    };

    const clear = async () => {
        setSaving(true);
        try {
            const updated = await patchUpload(file.id, {
                scale: "",
                scale_source: "",
            });
            onChange && onChange(updated);
            setOpen(false);
        } catch {
            toast.error("Couldn't clear scale");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="absolute bottom-3 right-3 z-20 pointer-events-auto"
            data-testid="scale-indicator"
        >
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        data-testid="scale-indicator-btn"
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-bold tabular-nums shadow-lg border backdrop-blur-md transition-colors ${
                            hasScale
                                ? "bg-[#1a3fd1]/90 border-[#1a3fd1]/50 text-white hover:bg-[#1a3fd1]/80"
                                : "bg-red-500/90 border-red-400 text-white hover:bg-red-500 animate-pulse"
                        }`}
                    >
                        {hasScale ? (
                            <>
                                <Ruler className="w-3.5 h-3.5" />
                                <span>{file.scale}</span>
                                {file.scale_source === "detected" && (
                                    <span className="text-[9px] uppercase tracking-wider opacity-80">
                                        auto
                                    </span>
                                )}
                            </>
                        ) : (
                            <>
                                <AlertTriangle className="w-3.5 h-3.5" />
                                <span>Set scale</span>
                            </>
                        )}
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    side="top"
                    className="w-72 p-0 bg-[#111] border border-white/15 text-white"
                    data-testid="scale-popover"
                >
                    <div className="px-3 py-2 border-b border-white/15 flex items-center justify-between">
                        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-300">
                            Drawing scale
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-[#8fa4f0]">
                            {unitSystem}
                        </span>
                    </div>
                    {/* iter-53: auto-detect from dimension annotations. */}
                    <button
                        type="button"
                        onClick={autoDetect}
                        disabled={detecting || saving}
                        data-testid="btn-autodetect-scale"
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider border-b border-white/15 text-emerald-300 hover:bg-emerald-500/10 transition-colors disabled:opacity-50"
                    >
                        {detecting ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Wand2 className="w-3.5 h-3.5" />
                        )}
                        <span>
                            {detecting
                                ? "Scanning dimensions…"
                                : "Auto-detect from dimensions"}
                        </span>
                    </button>
                    <div className="max-h-56 overflow-y-auto p-1" data-testid="scale-list">
                        {scales.map((s) => {
                            const active = file.scale === s;
                            return (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => save(s)}
                                    disabled={saving}
                                    data-testid={`scale-option-${s}`}
                                    className={`w-full flex items-center justify-between px-2.5 py-1.5 text-sm rounded-sm transition-colors ${
                                        active
                                            ? "bg-[#1a3fd1]/25 text-white"
                                            : "text-slate-200 hover:bg-white/10"
                                    }`}
                                >
                                    <span className="tabular-nums">{s}</span>
                                    {active && <Check className="w-3.5 h-3.5 text-[#8fa4f0]" />}
                                </button>
                            );
                        })}
                    </div>
                    <div className="p-2 border-t border-white/15 space-y-2">
                        <label className="block text-[10px] uppercase tracking-[0.12em] font-bold text-slate-400">
                            Custom
                        </label>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                save(custom);
                            }}
                            className="flex items-center gap-2"
                        >
                            <Input
                                value={custom}
                                onChange={(e) => setCustom(e.target.value)}
                                placeholder={
                                    unitSystem === "metric" ? "e.g. 1:150" : 'e.g. 1" = 15\''
                                }
                                data-testid="scale-custom-input"
                                className="h-8 text-sm bg-[#0a0a0a] border-white/15 focus-visible:ring-[#1a3fd1]"
                            />
                            <Button
                                type="submit"
                                size="sm"
                                disabled={saving || !custom.trim()}
                                data-testid="scale-custom-save"
                                className="h-8 bg-[#1a3fd1] hover:bg-[#1a3fd1]/80 text-white font-bold"
                            >
                                Save
                            </Button>
                        </form>
                        {hasScale && (
                            <button
                                type="button"
                                onClick={clear}
                                disabled={saving}
                                data-testid="scale-clear"
                                className="w-full text-xs text-slate-400 hover:text-red-300 flex items-center justify-center gap-1 py-1"
                            >
                                <X className="w-3 h-3" />
                                Clear scale
                            </button>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
